import React from "react";
import { useQuery, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";

import api from "api";
import { querykeys } from "api/querykeys";
import { useCheckTransitionsProvider } from "hooks";
import * as models from "models";

export function useAuthProvider() {
  const [user, setUser] = React.useState<models.Profile | null>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const checkTransactions = useCheckTransitionsProvider();

  const userQuery = useQuery(querykeys.loggedUser, () => api.getProfile(), {
    retry: false,
    onSuccess: (user) => setUser(user),
    onError: () => setUser(null),
  });

  const login = async (credentials: models.LoginCredentials) => {
    await api.auth.login(credentials);
    await userQuery.refetch();
  };

  const register = async (token: string, payload: models.RegisterPayload) => {
    const { transaction_token } = await api.auth.register(token, payload);
    await checkTransactions.intervalReload(transaction_token, api.auth.registerComplete);
    await userQuery.refetch();
  };

  const logout = async () => {
    await api.auth.logout();
    queryClient.clear();
    navigate("/login", { replace: true });
    setUser(null);
  };

  /**
   * Refresh interceptor has been removed as the user will be logged out after 30 days
   */

  return {
    user,
    isLoading: userQuery.isLoading,
    login,
    register,
    logout,
    refetch: userQuery.refetch,
  };
}
